// src/components/ErrorBoundary.tsx

/**
 * Global React error boundary for the Quantik Mind enterprise frontend.
 *
 * This component is responsible for:
 * - catching uncaught rendering errors in the React component tree
 * - logging diagnostic information to the browser console
 * - presenting a fallback UI to the user
 * - providing recovery actions through reload and home navigation
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ActionButton from './common/ActionButton';

/**
 * Props accepted by the error boundary component.
 */
interface Props {
  /** Descendant React tree protected by the boundary. */
  children: ReactNode;
}

/**
 * Internal error boundary state.
 */
interface State {
  /** Indicates whether an error has been captured. */
  hasError: boolean;

  /** Captured error instance, when available. */
  error: Error | null;
}

/**
 * Render a global fallback UI when an uncaught React rendering error occurs.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      hasError: false,
      error: null,
    };
  }

  /**
   * Update component state when an error is thrown by a descendant component.
   *
   * @param error - Captured rendering error.
   * @returns Updated fallback state.
   */
  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  /**
   * Capture diagnostic information for uncaught rendering errors.
   *
   * @param error - Captured rendering error.
   * @param errorInfo - React component stack metadata.
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary] Uncaught error:', error, errorInfo);
  }

  /**
   * Reload the current page.
   */
  handleReload = () => {
    window.location.reload();
  };

  /**
   * Redirect the user to the application home route.
   */
  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #0A0E14 0%, #13171F 100%)',
            p: 3,
          }}
        >
          <Paper
            elevation={24}
            sx={{
              maxWidth: 500,
              p: 4,
              borderRadius: 3,
              background: 'rgba(19, 23, 31, 0.95)',
              border: '1px solid rgba(255, 61, 61, 0.3)',
              textAlign: 'center',
            }}
          >
            <ErrorOutlineIcon
              sx={{
                fontSize: 64,
                color: '#FF3D3D',
                mb: 2,
              }}
            />

            <Typography variant="h5" fontWeight={700} color="white" mb={2}>
              Something went wrong
            </Typography>

            <Typography variant="body2" color="text.secondary" mb={3}>
              {this.state.error?.message || 'An unexpected error occurred'}
            </Typography>

            <Box display="flex" gap={2} justifyContent="center">
              <ActionButton
                onClick={this.handleReload}
              >
                Reload Page
              </ActionButton>

              <ActionButton
                onClick={this.handleGoHome}
              >
                Go Home
              </ActionButton>
            </Box>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  bgcolor: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: 1,
                  textAlign: 'left',
                  fontFamily: 'monospace',
                  fontSize: 11,
                  color: '#FF3D3D',
                  overflowX: 'auto',
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={700}
                  mb={1}
                  display="block"
                >
                  Error Stack (dev only):
                </Typography>
                {this.state.error.stack}
              </Box>
            )}
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}
